import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment-timezone";
import "moment/locale/en-gb";
import { StyledDateTextField } from "./style";
import { getMonthFromHeader, getWeekBounds, isWeekSelectableInRange } from "../../../api_calls/utils";

const PRIMARY_COLOR = "#AF3241";
const PRIMARY_COLOR_DARK = "#8a2833";

/**
 * Returns a user-facing validation message explaining why the date is invalid.
 * @param {moment.Moment} date - The date to validate
 * @param {moment.Moment|null} minMoment - Minimum allowed date
 * @param {moment.Moment|null} maxMoment - Maximum allowed date
 * @param {Function|undefined} enabledDates - Optional predicate for selectable dates
 * @param {string} prefix - Field name prefix for error messages
 * @returns {string} Validation message or empty string if valid
 */
const getValidationMessage = (date, minMoment, maxMoment, enabledDates, prefix) => {
  if (!date || !moment.isMoment(date) || !date.isValid()) {
    return "Please enter a valid date (DD/MM/YYYY)";
  }
  if (minMoment && date.isBefore(minMoment, "day")) {
    return minMoment.isSame(moment(), "day")
      ? prefix + " date cannot be in the past"
      : prefix + ` date cannot be before ${minMoment.format("DD/MM/YYYY")}`;
  }
  if (maxMoment && date.isAfter(maxMoment, "day")) {
    return maxMoment.isSame(moment(), "day")
      ? prefix + " date cannot be in the future"
      : prefix + ` date cannot be after ${maxMoment.format("DD/MM/YYYY")}`;
  }
  if (enabledDates) {
    const dateObj = new Date(date.year(), date.month(), date.date());
    if (!enabledDates(dateObj)) {
      return "This date is not available";
    }
  }
  return "";
};

/**
 * Derives field role from label for week selection callbacks
 * @param {string} label - The field label
 * @returns {'start' | 'end' | null} The derived field role
 */
const deriveFieldRole = (label) => {
  if (!label) return null;
  const lowerLabel = label.toLowerCase();
  if (lowerLabel.includes("start")) return "start";
  if (lowerLabel.includes("end")) return "end";
  return null;
};

/**
 * Generates slot props for MUI DatePicker with custom styling
 */
const getSlotProps = (onOpenPicker, isPickerOpen, onBlurHandler, inputRef, fieldError, showSelection, onKeyDownHandler) => ({
  textField: {
    variant: "standard",
    fullWidth: true,
    autoComplete: "off",
    onClick: onOpenPicker,
    onBlur: onBlurHandler,
    onKeyDown: onKeyDownHandler,
    inputRef: inputRef,
    error: fieldError !== "" && fieldError != null,
    helperText: fieldError !== "" && fieldError != null ? fieldError : "",
    FormHelperTextProps: {
      sx: {
        color: "#d32f2f",
        marginLeft: 0,
        fontSize: "0.75rem",
      },
    },
    InputProps: {
      readOnly: !isPickerOpen,
      sx: {
        cursor: isPickerOpen ? "text" : "pointer",
        caretColor: showSelection ? PRIMARY_COLOR : "transparent",
        "&:before": {
          borderBottomColor: "rgba(0, 0, 0, 0.42)",
        },
        "&:hover:not(.Mui-disabled):before": {
          borderBottomColor: "rgba(0, 0, 0, 0.87)",
        },
        "&:after": {
          borderBottomColor: PRIMARY_COLOR,
        },
        "& input": {
          cursor: isPickerOpen ? "text" : "pointer",
          caretColor: showSelection ? PRIMARY_COLOR : "transparent",
          "&::selection": {
            backgroundColor: showSelection ? "rgba(175, 50, 65, 0.3)" : "transparent",
          },
        },
      },
    },
    InputLabelProps: {
      sx: {
        "&.Mui-focused": {
          color: PRIMARY_COLOR,
        },
      },
    },
  },
  day: {
    sx: {
      width: 28,
      height: 28,
      fontSize: "0.75rem",
      "&:focus:not(.Mui-selected)": {
        backgroundColor: "transparent",
        outline: "none",
        "&:hover": {
          backgroundColor: "rgba(138, 40, 51, 0.2)",
        },
      },
      "&.Mui-selected": {
        backgroundColor: PRIMARY_COLOR,
        color: "#fff",
        "&:hover": { backgroundColor: PRIMARY_COLOR_DARK },
        "&:focus": { backgroundColor: PRIMARY_COLOR },
        "&.Mui-focusVisible": {
          backgroundColor: PRIMARY_COLOR,
          outline: `2px solid ${PRIMARY_COLOR_DARK}`,
          outlineOffset: -2,
        },
      },
      "&:hover": { backgroundColor: "rgba(138, 40, 51, 0.2)" },
      "&.Mui-focusVisible:not(.Mui-selected)": {
        outline: `2px solid ${PRIMARY_COLOR}`,
        outlineOffset: -2,
        backgroundColor: "rgba(175, 50, 65, 0.08)",
      },
      "&.MuiPickersDay-today": {
        border: "none",
        "&:not(.Mui-selected)": { border: "none" },
      },
      "&.MuiPickersDay-dayOutsideMonth:not(.Mui-disabled)": { color: "#777" },
      "&.Mui-disabled:not(.MuiPickersDay-dayOutsideMonth)": {
        color: "#ccc",
        opacity: 1,
        "&:hover": { cursor: "not-allowed" },
      },
      "&.Mui-disabled.MuiPickersDay-dayOutsideMonth": {
        color: "#e0e0e0",
        opacity: 0.5,
        cursor: "not-allowed",
      },
    },
  },
  calendarHeader: {
    sx: {
      paddingLeft: 1,
      paddingRight: 1,
      marginTop: 0,
      marginBottom: 0,
      minHeight: 36,
      "& .MuiPickersCalendarHeader-label": { fontSize: "0.875rem" },
      "& .MuiPickersCalendarHeader-switchViewButton": {
        color: PRIMARY_COLOR,
        padding: 0.5,
        "&:focus": { outline: `2px solid ${PRIMARY_COLOR}`, outlineOffset: -2 },
      },
      "& .MuiPickersArrowSwitcher-button": {
        color: PRIMARY_COLOR,
        padding: 0.5,
        "&:focus": { outline: `2px solid ${PRIMARY_COLOR}`, outlineOffset: -2 },
      },
    },
  },
  yearButton: {
    sx: {
      "&.Mui-selected": {
        backgroundColor: PRIMARY_COLOR,
        color: "#fff",
        "&:hover": { backgroundColor: PRIMARY_COLOR_DARK },
        "&:focus": { backgroundColor: PRIMARY_COLOR },
        "&.Mui-focusVisible": {
          backgroundColor: PRIMARY_COLOR,
          outline: `2px solid ${PRIMARY_COLOR_DARK}`,
          outlineOffset: -2,
        },
      },
      "&.Mui-focusVisible:not(.Mui-selected)": {
        outline: `2px solid ${PRIMARY_COLOR}`,
        outlineOffset: -2,
        backgroundColor: "rgba(175, 50, 65, 0.08)",
      },
    },
  },
  monthButton: {
    sx: {
      "&.Mui-selected": {
        backgroundColor: PRIMARY_COLOR,
        color: "#fff",
        "&:hover": { backgroundColor: PRIMARY_COLOR_DARK },
        "&:focus": { backgroundColor: PRIMARY_COLOR },
        "&.Mui-focusVisible": {
          backgroundColor: PRIMARY_COLOR,
          outline: `2px solid ${PRIMARY_COLOR_DARK}`,
          outlineOffset: -2,
        },
      },
      "&.Mui-focusVisible:not(.Mui-selected)": {
        outline: `2px solid ${PRIMARY_COLOR}`,
        outlineOffset: -2,
        backgroundColor: "rgba(175, 50, 65, 0.08)",
      },
    },
  },
  layout: {
    sx: {
      "& .MuiDateCalendar-root": { width: 260, height: "auto" },
      "& .MuiDayCalendar-header .MuiDayCalendar-weekDayLabel": {
        width: 28, height: 28, fontSize: "0.7rem", color: "#555",
      },
      "& .MuiDayCalendar-weekNumberLabel": {
        width: 28, fontSize: "0.7rem", color: "#555", fontWeight: 500, backgroundColor: "#f5f5f5",
      },
      "& .MuiDayCalendar-weekNumber": {
        width: 28, height: 28, fontSize: "0.7rem", cursor: "pointer",
        backgroundColor: "#f5f5f5", color: "#555",
        transition: "color 0.15s ease, background-color 0.15s ease",
        "&:hover": { color: PRIMARY_COLOR_DARK, fontWeight: 600, backgroundColor: "#e0e0e0" },
      },
      "& .MuiDayCalendar-weekNumber.week-number-disabled": {
        color: "#ccc", cursor: "not-allowed",
        "&:hover": { color: "#ccc", fontWeight: "normal" },
      },
      "& .MuiDayCalendar-slideTransition": { minHeight: 182, overflow: "hidden" },
      "& .MuiPickersDay-root": { width: 28, height: 28, color: "#000" },
      "& .MuiYearCalendar-root": {
        width: 260, maxHeight: 220, padding: "4px 0",
        "& .MuiPickersYear-yearButton": {
          width: 56, height: 28, fontSize: "0.8rem", margin: "2px 4px",
          "&.Mui-selected": {
            backgroundColor: PRIMARY_COLOR, color: "#fff",
            "&:hover": { backgroundColor: PRIMARY_COLOR_DARK },
          },
          "&.Mui-focusVisible:not(.Mui-selected)": {
            outline: `2px solid ${PRIMARY_COLOR}`, outlineOffset: -2,
            backgroundColor: "rgba(175, 50, 65, 0.08)",
          },
          "&.Mui-selected.Mui-focusVisible": {
            backgroundColor: PRIMARY_COLOR,
            outline: `2px solid ${PRIMARY_COLOR_DARK}`, outlineOffset: -2,
          },
        },
      },
      "& .MuiMonthCalendar-root": {
        width: 260, padding: "4px 0",
        "& .MuiPickersMonth-monthButton": {
          width: 56, height: 28, fontSize: "0.8rem", margin: "2px 4px",
          "&.Mui-selected": {
            backgroundColor: PRIMARY_COLOR, color: "#fff",
            "&:hover": { backgroundColor: PRIMARY_COLOR_DARK },
          },
          "&.Mui-disabled": { opacity: 0.4, cursor: "not-allowed" },
          "&.Mui-focusVisible:not(.Mui-selected)": {
            outline: `2px solid ${PRIMARY_COLOR}`, outlineOffset: -2,
            backgroundColor: "rgba(175, 50, 65, 0.08)",
          },
          "&.Mui-selected.Mui-focusVisible": {
            backgroundColor: PRIMARY_COLOR,
            outline: `2px solid ${PRIMARY_COLOR_DARK}`, outlineOffset: -2,
          },
        },
      },
    },
  },
  popper: {
    placement: "bottom-start",
    disableAutoFocus: true,
    modifiers: [{ name: "preventOverflow", options: { padding: 8 } }],
    sx: {
      zIndex: 1300,
      "& .MuiPaper-root": { boxShadow: "0 4px 20px rgba(0,0,0,0.15)" },
    },
  },
});

export default function DateInputField({
  label,
  elemId,
  fieldName,
  defaultVal,
  fieldRequired,
  onChangeCall,
  fieldError,
  minSelectable,
  maxSelectable,
  enabledDates,
  disabled,
  className,
  setDialogExpanded,
  onWeekSelect,
  weekSelectionMinDate,
  weekSelectionMaxDate,
  showDaysOutsideCurrentMonth,
}) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [pickerView, setPickerView] = useState("day");
  const [showSelection, setShowSelection] = useState(false);
  const [validationError, setValidationError] = useState("");
  const inputRef = useRef(null);
  const isClosingRef = useRef(false);
  const errorSetAtRef = useRef(null);
  const ERROR_IMMUNITY_MS = 2000;

  const enableWeekSelection = typeof onWeekSelect === "function";
  const fieldRole = deriveFieldRole(label);

  const effectiveMaxDate = maxSelectable
    ? moment(maxSelectable)
    : moment().add(2, "years").endOf("year");

  const minMoment = minSelectable ? moment(minSelectable) : null;

  const isDateValid = useCallback((date) => {
    if (!date || !moment.isMoment(date) || !date.isValid()) return false;
    if (minMoment && date.isBefore(minMoment, "day")) return false;
    if (effectiveMaxDate && date.isAfter(effectiveMaxDate, "day")) return false;
    if (enabledDates) {
      const dateObj = new Date(date.year(), date.month(), date.date());
      if (!enabledDates(dateObj)) return false;
    }
    return true;
  }, [minSelectable, effectiveMaxDate, enabledDates]);

  /**
  * Validates the current input. Clears the field on invalid input
  * and shows a descriptive error message.
  */
  const validateInput = useCallback(() => {
    if (!selectedDate) {
      // Respect error immunity period before clearing validation errors
      const errorAge = errorSetAtRef.current !== null
        ? Date.now() - errorSetAtRef.current
        : Infinity;
      if (errorAge > ERROR_IMMUNITY_MS) {
        setValidationError("");
      }
      return;
    }
    const message = getValidationMessage(selectedDate, minMoment, effectiveMaxDate, enabledDates, label.split(" ")[0]);
    if (message) {
      setValidationError(message);
      errorSetAtRef.current = Date.now();
      setSelectedDate(null);
      onChangeCall(null);
    } else {
      setValidationError("");
    }
  }, [selectedDate, minMoment, effectiveMaxDate, enabledDates, onChangeCall, label]);

  /**
   * Closes the picker, validates input, and ensures the input is fully
   * blurred after MUI's async focus restoration completes.
   * Uses dual-timeout approach for reliable blur in edge cases.
   */
  const closePicker = useCallback(() => {
    isClosingRef.current = true;
    validateInput();
    setIsOpen(false);
    setPickerView("day");
    window.getSelection()?.removeAllRanges();
    if (setDialogExpanded) {
      setDialogExpanded(false);
    }
    // Primary blur attempt after MUI's typical focus restoration
    setTimeout(() => {
      inputRef.current?.blur();
    }, 100);
    // Secondary fallback for edge cases where validation triggers delayed focus
    setTimeout(() => {
      inputRef.current?.blur();
      isClosingRef.current = false;
    }, 300);
  }, [validateInput, setDialogExpanded]);

  /**
   * Handles input blur - validates the input.
   * Skipped when closePicker already performed validation.
   */
  const handleInputBlur = useCallback(() => {
    if (isClosingRef.current) return;
    validateInput();
  }, [validateInput]);

  const handleDateChange = (newValue) => {
    if (!newValue || !newValue.isValid()) return;
    const dateObj = new Date(newValue.year(), newValue.month(), newValue.date(), 0, 0, 0);
    setSelectedDate(newValue);
    if (isDateValid(newValue)) {
      setValidationError("");
    }
    onChangeCall(dateObj);
    if (setDialogExpanded) {
      setDialogExpanded(true);
    }
  };

  const handleOpen = useCallback(() => {
    if (disabled) return;
    const errorAge = errorSetAtRef.current !== null
      ? Date.now() - errorSetAtRef.current
      : Infinity;
    if (errorAge > ERROR_IMMUNITY_MS) {
      setValidationError("");
    }
    setIsOpen(true);
    if (setDialogExpanded) {
      setDialogExpanded(true);
    }
  }, [disabled, setDialogExpanded]);

  const preventScrollOnViewChange = useCallback(() => {
    const currentScrollPos = window.scrollY;
    requestAnimationFrame(() => {
      window.scrollTo(0, currentScrollPos);
    });
  }, []);

  useEffect(() => {
    if (defaultVal) {
      const newDate = moment(defaultVal);
      setSelectedDate(newDate);
      setValidationError("");
    } else {
      setSelectedDate(null);
    }
  }, [defaultVal]);

  /**
   * Enter closes the picker identically to clicking outside
   */
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (isOpen) {
        closePicker();
      }
    } else if (e.key === "Tab" && isOpen) {
      closePicker();
    }
  }, [isOpen, closePicker]);

  useEffect(() => {
    setShowSelection(isOpen);
  }, [isOpen]);

  /**
   * One-shot focus reclaim after MUI's popper steals focus on open.
   * Guarded by isClosingRef so it never fires during close.
   */
  useEffect(() => {
    if (!isOpen) return;
    const timerId = setTimeout(() => {
      if (isClosingRef.current) return;
      inputRef.current?.focus();
    }, 50);
    return () => clearTimeout(timerId);
  }, [isOpen]);

  const updateWeekNumberStyling = useCallback(() => {
    if (!enableWeekSelection) return;
    const weekNumbers = document.querySelectorAll(".MuiDayCalendar-weekNumber");
    weekNumbers.forEach((weekEl) => {
      const weekText = weekEl.textContent;
      if (!weekText) return;
      const weekNumber = parseInt(weekText, 10);
      if (isNaN(weekNumber)) return;
      const popper = weekEl.closest(".MuiPickersPopper-root");
      if (!popper) return;
      const monthHeader = popper.querySelector(".MuiPickersCalendarHeader-label");
      if (!monthHeader) return;
      const headerText = monthHeader.textContent || "";
      const yearMatch = headerText.match(/\d{4}/);
      const displayedYear = yearMatch ? parseInt(yearMatch[0], 10) : new Date().getFullYear();
      const displayedMonth = getMonthFromHeader(headerText);
      const isSelectable = isWeekSelectableInRange(weekNumber, displayedYear, displayedMonth, weekSelectionMinDate, weekSelectionMaxDate);
      if (isSelectable) {
        weekEl.classList.remove("week-number-disabled");
      } else {
        weekEl.classList.add("week-number-disabled");
      }
    });
  }, [enableWeekSelection, weekSelectionMinDate, weekSelectionMaxDate]);

  useEffect(() => {
    if (!enableWeekSelection || !isOpen) return;
    const handleWeekClick = (e) => {
      const target = e.target;
      if (!target || !target.classList.contains("MuiDayCalendar-weekNumber")) return;
      e.preventDefault();
      e.stopPropagation();
      if (target.classList.contains("week-number-disabled")) return;
      const weekText = target.textContent;
      if (!weekText) return;
      const weekNumber = parseInt(weekText, 10);
      if (isNaN(weekNumber)) return;
      const popper = target.closest(".MuiPickersPopper-root");
      if (!popper) return;
      const monthHeader = popper.querySelector(".MuiPickersCalendarHeader-label");
      if (!monthHeader) return;
      const headerText = monthHeader.textContent || "";
      const yearMatch = headerText.match(/\d{4}/);
      const displayedYear = yearMatch ? parseInt(yearMatch[0], 10) : new Date().getFullYear();
      const displayedMonth = getMonthFromHeader(headerText);

      const { monday, sunday } = getWeekBounds(weekNumber, displayedYear, displayedMonth);
      const anchorDate = fieldRole === "end" ? sunday : monday;
      const weekMoment = moment(anchorDate);
      setSelectedDate(weekMoment);
      setValidationError("");
      onChangeCall(weekMoment.toDate());

      // Close picker and blur input using same pattern as closePicker
      isClosingRef.current = true;
      setIsOpen(false);
      window.getSelection()?.removeAllRanges();
      if (setDialogExpanded) {
        setDialogExpanded(false);
      }

      // Dual-timeout blur for reliable focus removal
      setTimeout(() => {
        inputRef.current?.blur();
      }, 150);
      setTimeout(() => {
        inputRef.current?.blur();
        isClosingRef.current = false;
      }, 600);

      onWeekSelect(monday, sunday, fieldRole);
    };
    document.addEventListener("mousedown", handleWeekClick, true);
    return () => document.removeEventListener("mousedown", handleWeekClick, true);
  }, [enableWeekSelection, isOpen, onWeekSelect, setDialogExpanded, onChangeCall, fieldRole]);

  useEffect(() => {
    if (!enableWeekSelection || !isOpen) return;
    const timeoutId = setTimeout(updateWeekNumberStyling, 50);
    const observer = new MutationObserver(() => updateWeekNumberStyling());
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [enableWeekSelection, isOpen, updateWeekNumberStyling]);

  const shouldDisableDate = enabledDates
    ? (date) => {
      const dateObj = new Date(date.year(), date.month(), date.date());
      return !enabledDates(dateObj);
    }
    : undefined;

  const effectiveError = validationError || fieldError || "";
  const slotProps = getSlotProps(handleOpen, isOpen, handleInputBlur, inputRef, effectiveError, showSelection, handleKeyDown);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="en-gb">
      <Box width="100%">
        <DesktopDatePicker
          sx={{ mt: 0.125 }}
          label={label}
          value={selectedDate}
          onChange={handleDateChange}
          open={isOpen}
          onOpen={() => {
            if (!disabled) {
              const errorAge = errorSetAtRef.current !== null
                ? Date.now() - errorSetAtRef.current
                : Infinity;
              if (errorAge > ERROR_IMMUNITY_MS) {
                setValidationError("");
              }
              setIsOpen(true);
              if (setDialogExpanded) {
                setDialogExpanded(true);
              }
            }
          }}
          onClose={closePicker}
          view={pickerView}
          onViewChange={(newView) => {
            setPickerView(newView);
            preventScrollOnViewChange();
          }}
          format="DD/MM/YYYY"
          disabled={disabled}
          minDate={minSelectable ? moment(minSelectable) : undefined}
          maxDate={effectiveMaxDate}
          shouldDisableDate={shouldDisableDate}
          displayWeekNumber
          showDaysOutsideCurrentMonth
          views={["year", "month", "day"]}
          openTo="day"
          slots={{ textField: StyledDateTextField }}
          slotProps={slotProps}
        />
      </Box>
    </LocalizationProvider>
  );
}
